import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

interface TournamentResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  p1Name: string;
  p2Name: string;
  p1Wins: number | null;
  p2Wins: number | null;
  onSubmit: (p1Wins: number, p2Wins: number) => Promise<void>;
}

const WINS_OPTIONS = [
  { value: 0, label: "0" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 999, label: "欠場" },
];

const isByePlayer = (name: string): boolean =>
  name.trim().toLowerCase() === "bye";

const TournamentResultModal: React.FC<TournamentResultModalProps> = ({
  isOpen,
  onClose,
  p1Name,
  p2Name,
  p1Wins,
  p2Wins,
  onSubmit,
}) => {
  const [localP1Wins, setLocalP1Wins] = useState<number>(p1Wins ?? 0);
  const [localP2Wins, setLocalP2Wins] = useState<number>(p2Wins ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const p1IsBye = isByePlayer(p1Name);
  const p2IsBye = isByePlayer(p2Name);
  const hasByeMatch = p1IsBye || p2IsBye;

  useEffect(() => {
    setLocalP1Wins(p1Wins ?? 0);
    setLocalP2Wins(p2Wins ?? 0);
  }, [p1Wins, p2Wins, isOpen]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(localP1Wins, localP2Wins);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleByeWinSubmit = async () => {
    if (!hasByeMatch) return;

    const p1AutoWins = p2IsBye ? 2 : 0;
    const p2AutoWins = p1IsBye ? 2 : 0;

    setIsSubmitting(true);
    try {
      await onSubmit(p1AutoWins, p2AutoWins);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-96 p-6 animate-in fade-in duration-300">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">
            試合結果登録
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-300 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {/* Player 1 */}
          <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="text-sm font-bold text-slate-700 flex-1">
              {p1Name}
            </span>
            <select
              value={localP1Wins}
              onChange={(e) => setLocalP1Wins(Number(e.target.value))}
              disabled={isSubmitting}
              className="bg-white border border-slate-300 text-slate-900 font-black rounded px-2 py-1 outline-none cursor-pointer text-lg w-16 text-center"
            >
              {WINS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* VS */}
          <div className="flex justify-center py-2">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">
              VS
            </span>
          </div>

          {/* Player 2 */}
          <div className="flex justify-between items-center px-4 py-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="text-sm font-bold text-slate-700 flex-1">
              {p2Name}
            </span>
            <select
              value={localP2Wins}
              onChange={(e) => setLocalP2Wins(Number(e.target.value))}
              disabled={isSubmitting}
              className="bg-white border border-slate-300 text-slate-900 font-black rounded px-2 py-1 outline-none cursor-pointer text-lg w-16 text-center"
            >
              {WINS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-3">
          {hasByeMatch && (
            <button
              onClick={handleByeWinSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              不戦勝（2-0）
            </button>
          )}
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-bold text-sm rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            {isSubmitting ? "登録中..." : "反映"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentResultModal;
